import * as React from 'react';
import { Button, FormGroup, FormSelect, FormSelectOption, Label, Title } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { relaxedBase } from '../../../../shared/riskregister/filters';
import { laneRows } from '../../../../shared/riskregister/lanes';
import { sortRows } from '../../../../shared/riskregister/sorting';
import type { Lane, Payload, Row, ViewState } from '../../../../shared/riskregister/types';
import { DueChip, IdleChip, ScoreChip } from '../common/Chips';

interface LaneDefinition {
  title: string;
  why: (payload: Payload, since: number) => string;
  activity: (rows: Row[], since: number) => number;
  activityLabel: string;
  jql: (payload: Payload, since: number) => string;
}

const LANE_DEFINITIONS: Record<Lane, LaneDefinition> = {
  intake: {
    title: 'Triage new intake',
    why: (_payload, since) => `Items labeled for the register within the last ${since} days.`,
    activity: (rows, since) => rows.filter((row) => row.entered_days !== null && row.entered_days <= since).length,
    activityLabel: 'new arrivals',
    jql: (_payload, since) => `labels = RiskRegister AND labels changed after -${since}d`,
  },
  score: {
    title: 'Score it',
    why: () => 'Open items without a measured risk score.',
    activity: (rows, since) => rows.filter((row) => row.d_score !== null && row.d_score <= since).length,
    activityLabel: 'scored',
    jql: () => 'labels = RiskRegister AND statusCategory != Done AND "Risk score" is EMPTY',
  },
  cat: {
    title: 'Classify it',
    why: () => 'Open items without a RiskRegister category.',
    activity: (rows, since) => rows.filter((row) => row.d_cat !== null && row.d_cat <= since).length,
    activityLabel: 'categorised',
    jql: () => 'labels = RiskRegister AND statusCategory != Done AND "Risk category" is EMPTY',
  },
  own: {
    title: 'Own it',
    why: (payload) => `Open unassigned items outside the ${payload.own_grace}-day ownership grace period.`,
    activity: (rows, since) => rows.filter((row) => row.d_asg !== null && row.d_asg <= since).length,
    activityLabel: 'assigned',
    jql: () => 'labels = RiskRegister AND statusCategory != Done AND assignee is EMPTY',
  },
  redate: {
    title: 'Re-date or re-scope',
    why: (payload) => `Open overdue items with at least ${payload.redate_slips} due-date slips.`,
    activity: (rows, since) => rows.filter((row) => row.d_due !== null && row.d_due <= since).length,
    activityLabel: 'due dates changed',
    jql: () => 'labels = RiskRegister AND statusCategory != Done AND due < now()',
  },
  limbo: {
    title: 'Unblock',
    why: (payload) => `Open, not-started, unassigned items idle for at least ${payload.stale_days} days.`,
    activity: (rows, since) => rows.filter((row) => row.d_status !== null && row.d_status <= since).length,
    activityLabel: 'status moved',
    // Mirrors isLimbo(): open, not-started (statusCategory "To Do"), unassigned, and idle for at
    // least stale_days. This previously duplicated the "Own it" JQL, so the link showed a wider
    // population than the count beside it.
    jql: (payload) =>
      'labels = RiskRegister AND statusCategory = "To Do" AND assignee is EMPTY '
      + `AND NOT status CHANGED AFTER -${payload.stale_days}d`,
  },
};

function jiraSearch(payload: Payload, jql: string): string {
  return `${payload.jira_search}${encodeURIComponent(jql)}`;
}

function keyListSearch(payload: Payload, rows: Row[]): string {
  const keys = rows.slice(0, 150).map((row) => row.key);
  return jiraSearch(payload, keys.length ? `key in (${keys.join(', ')})` : 'key is EMPTY');
}

export const ReviewQueuePanel: React.FunctionComponent<{
  payload: Payload;
  state: ViewState;
  onChange: (change: Partial<ViewState>) => void;
}> = ({ payload, state, onChange }) => {
  const [copyStatus, setCopyStatus] = React.useState('');
  const since = Number(state.since);
  const baseRows = relaxedBase(payload.rows, state, payload);
  const resolved = baseRows.filter(
    (row) =>
      row.resolved &&
      row.resolved >= new Date(Date.parse(`${payload.as_of}T00:00:00Z`) - since * 86400000).toISOString().slice(0, 10),
  ).length;
  const activity = {
    arrivals: LANE_DEFINITIONS.intake.activity(baseRows, since),
    scored: LANE_DEFINITIONS.score.activity(baseRows, since),
    categorised: LANE_DEFINITIONS.cat.activity(baseRows, since),
    assigned: LANE_DEFINITIONS.own.activity(baseRows, since),
    statusMoved: LANE_DEFINITIONS.limbo.activity(baseRows, since),
    resolved,
  };
  const laneCollections = payload.lane_order.map((lane) => ({
    lane,
    definition: LANE_DEFINITIONS[lane],
    rows: sortRows(laneRows(baseRows, lane, state, payload), 'worst'),
  }));
  const copyText = [
    `Risk Register review queue as of ${payload.as_of}; changes in the last ${since} days.`,
    `New arrivals ${activity.arrivals}; scored ${activity.scored}; categorised ${activity.categorised}; assigned ${activity.assigned}; status moved ${activity.statusMoved}; resolved ${activity.resolved}.`,
    ...laneCollections.map(
      ({ definition, rows }) =>
        `${definition.title}: ${rows.length}${rows.length ? ` (${rows.map((row) => row.key).join(', ')})` : ''}`,
    ),
  ].join('\n');

  const copyQueue = async () => {
    if (!navigator.clipboard?.writeText) {
      setCopyStatus('Clipboard access is unavailable.');
      return;
    }
    try {
      await navigator.clipboard.writeText(copyText);
      setCopyStatus('Queue copied.');
    } catch {
      setCopyStatus('Unable to copy the queue.');
    }
  };

  return (
    <div className="risk-register__panel-stack">
      <div className="risk-register__queue-toolbar">
        <FormGroup label="Since last session" fieldId="risk-register-queue-since">
          <FormSelect
            id="risk-register-queue-since"
            value={state.since}
            onChange={(_event, sinceValue) => onChange({ since: sinceValue as ViewState['since'] })}
          >
            {['7', '14', '30'].map((option) => (
              <FormSelectOption key={option} value={option} label={`Last ${option} days`} />
            ))}
          </FormSelect>
        </FormGroup>
        <Button variant="secondary" onClick={() => void copyQueue()}>
          Copy as text
        </Button>
        {copyStatus && <span aria-live="polite">{copyStatus}</span>}
      </div>
      <p className="risk-register__panel-summary">
        Changed in the last {since} days: <strong>{activity.arrivals}</strong> new arrivals,{' '}
        <strong>{activity.scored}</strong> scored, <strong>{activity.categorised}</strong> categorised,{' '}
        <strong>{activity.assigned}</strong> assigned, <strong>{activity.statusMoved}</strong> status moved, and{' '}
        <strong>{activity.resolved}</strong> resolved.
      </p>
      {laneCollections.map(({ lane, definition, rows }) => {
        const isExpanded = state.laneall === lane;
        const visible = isExpanded ? rows : rows.slice(0, 8);
        const activityCount = definition.activity(baseRows, since);
        return (
          <div key={lane} className="risk-register__queue-lane">
            <div className="risk-register__queue-lane-header">
              <Title headingLevel="h3" size="md">
                {definition.title}
              </Title>
              <Label
                className={`risk-register__chip ${rows.length ? 'risk-register__chip--danger' : 'risk-register__chip--success'}`}
                color={rows.length ? 'red' : 'green'}
                isCompact
              >
                {rows.length} item{rows.length === 1 ? '' : 's'}
              </Label>
              <span>
                {activityCount} {definition.activityLabel} since last session
              </span>
            </div>
            <p>{definition.why(payload, since)}</p>
            <div className="risk-register__link-row">
              <a href={jiraSearch(payload, definition.jql(payload, since))} target="_blank" rel="noreferrer">
                JQL in Jira
              </a>
              <a href={keyListSearch(payload, rows)} target="_blank" rel="noreferrer">
                Matching key list ({Math.min(rows.length, 150)}
                {rows.length > 150 ? ' of 150 cap' : ''})
              </a>
            </div>
            {!rows.length ? (
              <p className="risk-register__empty">Nothing to review in this lane.</p>
            ) : (
              <>
                <div className="risk-register__table-wrap">
                  <Table aria-label={`${definition.title} review queue`} variant="compact">
                    <Thead>
                      <Tr>
                        <Th scope="col">Ticket</Th>
                        <Th scope="col">Summary</Th>
                        <Th scope="col">Due</Th>
                        <Th scope="col">Idle</Th>
                        <Th scope="col">Owner</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {visible.map((row) => (
                        <Tr key={row.key}>
                          <Td dataLabel="Ticket">
                            <a href={`${payload.jira}${row.key}`} target="_blank" rel="noreferrer">
                              {row.key}
                            </a>
                          </Td>
                          <Td dataLabel="Summary">
                            {row.summary} <ScoreChip row={row} />
                          </Td>
                          <Td dataLabel="Due">
                            <DueChip row={row} />
                          </Td>
                          <Td dataLabel="Idle">
                            <IdleChip row={row} staleDays={payload.stale_days} stalledDays={payload.stalled_days} />
                          </Td>
                          <Td dataLabel="Owner">{row.assignee || 'Unassigned'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
                {rows.length > 8 && (
                  <Button
                    variant="link"
                    isInline
                    onClick={() => onChange({ laneall: isExpanded ? '' : lane })}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? 'Show first 8 items' : `Show all ${rows.length} items`}
                  </Button>
                )}
              </>
            )}
          </div>
        );
      })}
      <p className="risk-register__note">
        Queue lanes use the relaxed historical base: scope, stage, due, and other state filters do not hide work;
        platform area, accountability drill-down, and search still apply.
      </p>
      <p className="risk-register__note">
        Lanes are separate questions and can overlap. The displayed count and key list always come from the same lane
        test; Jira links list no more than 150 keys.
      </p>
    </div>
  );
};
